'use client';

import { cva } from 'class-variance-authority';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { cn } from '../../../lib/utils';
import { ChevronDown, Close } from '../../../icons';
import { Icon, type SvgIcon } from '../../icon/icon';
import {
  inputBaseLayout,
  inputBaseTypography,
  inputBaseTransition,
  inputBaseRing,
  inputErrorVariant,
  inputDisabledVariant,
  inputWithLeftElementVariant,
} from '../../input/input-variants';
import { useFieldContext } from '../../field/field';

const comboboxInputVariants = cva(
  cn(
    inputBaseLayout,
    inputBaseTypography,
    'placeholder:text-space-60',
    'overflow-hidden text-ellipsis',
    inputBaseTransition,
    'disabled:cursor-not-allowed',
    inputBaseRing,
    'focus:bg-white focus:ring-2',
    'pr-9',
  ),
  {
    variants: {
      variant: {
        default: cn('bg-space-04', 'group-hover/combobox:not-focus:not-disabled:bg-space-08'),
        white: cn(
          'border border-space-40 bg-white',
          'group-hover/combobox:not-focus:not-disabled:bg-space-04',
          'focus:border-transparent',
        ),
      },
      error: inputErrorVariant,
      disabled: inputDisabledVariant,
      withLeftElement: inputWithLeftElementVariant,
    },
    defaultVariants: {
      variant: 'default',
      error: false,
      disabled: false,
      withLeftElement: false,
    },
  },
);

export type ComboboxInputProps = Omit<ComboboxPrimitive.Input.Props, 'className'> & {
  variant?: 'default' | 'white';
  error?: boolean;
  showClear?: boolean;
  showChevron?: boolean;
  /**
   * Icon displayed on the left side of the input.
   *
   * Only supported for single-selection comboboxes (`ComboboxInput`).
   * For multiple selection, use `ComboboxChips` which does not support icons
   * because the container grows vertically with chips.
   */
  icon?: SvgIcon;
  className?: string;
  classNames?: {
    wrapper?: string;
  };
};

export const ComboboxInput = ({
  variant = 'default',
  error: errorProp,
  disabled: disabledProp,
  showClear = false,
  showChevron = true,
  icon,
  className,
  classNames,
  ...props
}: ComboboxInputProps) => {
  const fieldContext = useFieldContext();
  const error = errorProp ?? fieldContext?.error ?? false;
  const disabled = disabledProp ?? fieldContext?.disabled ?? false;
  const rightPadding =
    showClear && showChevron ? 'pr-20' : showClear ? 'pr-10' : showChevron ? 'pr-9' : '';

  return (
    <div className={cn('group/combobox relative', classNames?.wrapper)}>
      {icon && (
        <Icon
          icon={icon}
          className="pointer-events-none absolute top-1/2 left-4 z-10 size-[16px] -translate-y-1/2 text-space"
        />
      )}
      <ComboboxPrimitive.Input
        data-slot="combobox-input"
        disabled={disabled}
        className={cn(
          comboboxInputVariants({ variant, error, disabled, withLeftElement: !!icon }),
          rightPadding,
          className,
        )}
        {...props}
      />

      {showClear && (
        <ComboboxPrimitive.Clear
          data-slot="combobox-clear"
          disabled={disabled}
          className={cn(
            'absolute top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-lg',
            'text-space-60 transition-colors hover:not-disabled:text-space disabled:cursor-not-allowed',
            'focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:outline-none',
            showChevron ? 'right-8' : 'right-2',
          )}>
          <Icon icon={Close} className="size-4" />
        </ComboboxPrimitive.Clear>
      )}

      {showChevron && (
        <ComboboxPrimitive.Trigger
          data-slot="combobox-trigger"
          disabled={disabled}
          className={cn(
            'absolute top-1/2 right-3 -translate-y-1/2 text-space-60',
            'transition-transform duration-150',
            'disabled:cursor-not-allowed',
          )}>
          <Icon icon={ChevronDown} className="size-4" />
        </ComboboxPrimitive.Trigger>
      )}
    </div>
  );
};
